import { SectionError, SectionLoading } from '@/components/section-state';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { BarChart3 } from 'lucide-react';
import { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts';
import { formatDate, formatTrendValue, getQualityGapCount, getTrendLabel, getTrendValueKey } from './formatting';
import type {
  UsageClinicRow,
  UsageTimelineRow,
  UsageTrendMetric,
  UsageTrendSeries,
  UsageTrendView,
  UsageUserRow,
} from './types';

const comparisonPalette = [
  'var(--primary)',
  'hsl(160 65% 36%)',
  'hsl(28 85% 46%)',
  'hsl(340 70% 52%)',
  'hsl(199 80% 45%)',
  'hsl(45 90% 45%)',
  'hsl(270 55% 58%)',
  'hsl(12 75% 50%)',
];

const getComparisonSeriesKey = (index: number) => `series_${index}`;

const trendConfig = {
  total_cost_usd: { label: 'Heildarkostnaður', color: 'var(--primary)' },
  total_cost_per_audio_minute: { label: 'Kostnaður á hljóðmínútu', color: 'var(--primary)' },
  total_events: { label: 'Atburðir', color: 'var(--primary)' },
  data_gaps: { label: 'Gagnagöt', color: 'hsl(var(--destructive))' },
};

const emptyMetricSet = {
  total_cost_usd: 0,
  audio_minutes: 0,
  total_cost_per_audio_minute: 0,
  asr_minutes: 0,
  asr_cost_per_minute: 0,
  total_tokens: 0,
  total_events: 0,
  failed_events: 0,
  missing_cost_events: 0,
  missing_audio_duration_events: 0,
};

export const UsageTrendCard = ({
  timeline,
  series,
  view,
  metric,
  isLoading,
  isError,
  error,
  onRetry,
  onViewChange,
  onMetricChange,
  onClinicFilter,
  onUserFilter,
}: {
  timeline: UsageTimelineRow[];
  series: UsageTrendSeries[];
  view: UsageTrendView;
  metric: UsageTrendMetric;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  onViewChange: (view: UsageTrendView) => void;
  onMetricChange: (metric: UsageTrendMetric) => void;
  onClinicFilter: (clinic: UsageClinicRow) => void;
  onUserFilter: (user: UsageUserRow) => void;
}) => {
  const valueKey = getTrendValueKey(metric);
  const isComparisonView = view !== 'overall';
  const label = getTrendLabel(metric);
  const chartLabel = isComparisonView
    ? `Þróun ${label} eftir ${view === 'clinic' ? 'stofu' : 'notanda'}`
    : `Þróun ${label}`;

  const overallChartData = useMemo(
    () =>
      timeline.map((row) => ({
        ...row,
        dateLabel: formatDate(row.date),
        data_gaps: getQualityGapCount(row),
      })),
    [timeline]
  );

  const comparisonChartConfig = useMemo(
    () =>
      Object.fromEntries(
        series.map((item, index) => [
          getComparisonSeriesKey(index),
          {
            label: item.label || 'Óþekkt',
            color: comparisonPalette[index % comparisonPalette.length],
          },
        ])
      ),
    [series]
  );

  const comparisonChartData = useMemo(() => {
    const dates = Array.from(new Set(series.flatMap((item) => item.points.map((point) => point.date)))).sort();

    return dates.map((date) => {
      const row: Record<string, number | string> = { date, dateLabel: formatDate(date) };
      series.forEach((item, index) => {
        const point = item.points.find((candidate) => candidate.date === date);
        row[getComparisonSeriesKey(index)] = point?.value ?? 0;
      });
      return row;
    });
  }, [series]);

  const handleSeriesFilter = (item: UsageTrendSeries) => {
    if (item.id == null) return;

    if (view === 'clinic') {
      onClinicFilter({
        ...emptyMetricSet,
        clinic_id: item.id,
        clinic_name: item.label,
        total_cost_usd: item.total_cost_usd,
        total_events: item.total_events,
      });
      return;
    }

    onUserFilter({
      ...emptyMetricSet,
      user_id: item.id,
      name: item.label,
      email: null,
      clinic_id: null,
      clinic_name: null,
      total_cost_usd: item.total_cost_usd,
      total_events: item.total_events,
    });
  };

  const latestRows = isComparisonView
    ? series.map((item) => ({
        label: item.label || 'Óþekkt',
        value: item.points.at(-1)?.value ?? 0,
      }))
    : [
        {
          label,
          value: overallChartData.at(-1)?.[valueKey as keyof (typeof overallChartData)[number]] as number | undefined,
        },
      ];

  return (
    <Card>
      <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <CardTitle>Þróun</CardTitle>
          <CardDescription>
            {isComparisonView ? `Efstu ${view === 'clinic' ? 'stofur' : 'notendur'}` : 'Samtals'}
          </CardDescription>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="inline-flex w-full rounded-md border bg-muted p-1 sm:w-auto" aria-label="Þróun">
            <Button
              type="button"
              variant={view === 'overall' ? 'secondary' : 'ghost'}
              className={cn('h-8 flex-1 px-3 sm:flex-none', view === 'overall' && 'shadow-sm')}
              aria-pressed={view === 'overall'}
              onClick={() => onViewChange('overall')}
            >
              Samtals
            </Button>
            <Button
              type="button"
              variant={view === 'clinic' ? 'secondary' : 'ghost'}
              className={cn('h-8 flex-1 px-3 sm:flex-none', view === 'clinic' && 'shadow-sm')}
              aria-pressed={view === 'clinic'}
              onClick={() => onViewChange('clinic')}
            >
              Stofa
            </Button>
            <Button
              type="button"
              variant={view === 'user' ? 'secondary' : 'ghost'}
              className={cn('h-8 flex-1 px-3 sm:flex-none', view === 'user' && 'shadow-sm')}
              aria-pressed={view === 'user'}
              onClick={() => onViewChange('user')}
            >
              Notandi
            </Button>
          </div>
          <Select value={metric} onValueChange={(value) => onMetricChange(value as UsageTrendMetric)}>
            <SelectTrigger className="w-full sm:w-[220px]" aria-label="Þróunarmælikvarði">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="cost">Heildarkostnaður</SelectItem>
              <SelectItem value="cost_per_audio_minute">Kostnaður á hljóðmínútu</SelectItem>
              <SelectItem value="events">Atburðir</SelectItem>
              <SelectItem value="data_gaps">Gagnagöt</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && <SectionLoading label="Sæki þróun" />}
        {isError && <SectionError title="Þróun hlóðst ekki" error={error} onRetry={onRetry} />}
        {!isLoading && !isError && !isComparisonView && (
          <div role="img" aria-label={chartLabel}>
            <ChartContainer config={trendConfig} className="h-[320px] w-full">
              <LineChart data={overallChartData} margin={{ left: 12, right: 12, top: 12 }}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="dateLabel" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis
                  tickLine={false}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickMargin={8}
                  width={metric === 'cost_per_audio_minute' ? 84 : 56}
                  tickFormatter={(value) => formatTrendValue(Number(value), metric)}
                />
                <ChartTooltip
                  allowEscapeViewBox={{ x: true, y: true }}
                  content={<ChartTooltipContent valueFormatter={(value) => formatTrendValue(Number(value), metric)} />}
                />
                <Line
                  dataKey={valueKey}
                  type="monotone"
                  stroke={`var(--color-${valueKey})`}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ChartContainer>
          </div>
        )}
        {!isLoading && !isError && isComparisonView && comparisonChartData.length > 0 && (
          <div role="img" aria-label={chartLabel}>
            <ChartContainer config={comparisonChartConfig} className="h-[320px] w-full">
              <LineChart data={comparisonChartData} margin={{ left: 12, right: 12, top: 12 }}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="dateLabel" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis
                  tickLine={false}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  tickMargin={8}
                  width={metric === 'cost_per_audio_minute' ? 84 : 56}
                  tickFormatter={(value) => formatTrendValue(Number(value), metric)}
                />
                <ChartTooltip
                  allowEscapeViewBox={{ x: true, y: true }}
                  content={
                    <ChartTooltipContent
                      indicator="line"
                      valueFormatter={(value) => formatTrendValue(Number(value), metric)}
                    />
                  }
                />
                {series.map((item, index) => {
                  const key = getComparisonSeriesKey(index);
                  return (
                    <Line
                      key={item.id ?? `${item.label}-${index}`}
                      dataKey={key}
                      name={item.label || 'Óþekkt'}
                      type="monotone"
                      stroke={`var(--color-${key})`}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  );
                })}
              </LineChart>
            </ChartContainer>
          </div>
        )}
        {!isLoading && !isError && isComparisonView && comparisonChartData.length === 0 && (
          <div className="flex h-[320px] items-center justify-center rounded-md border border-dashed text-muted-foreground text-sm">
            Engin gögn fundust fyrir valið tímabil.
          </div>
        )}
        {!isLoading && !isError && latestRows.length > 0 && (
          <table className="sr-only">
            <caption>{chartLabel}</caption>
            <thead>
              <tr>
                <th>Röð</th>
                <th>Síðasta gildi</th>
              </tr>
            </thead>
            <tbody>
              {latestRows.map((row) => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td>{formatTrendValue(row.value ?? 0, metric)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!isLoading && !isError && isComparisonView && series.length > 0 && (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
            {series.map((item, index) => (
              <Button
                key={item.id ?? `${item.label}-${index}`}
                type="button"
                variant="ghost"
                disabled={item.id == null}
                className="h-auto min-h-12 justify-start gap-2 px-2 py-2 text-left"
                aria-label={`Sía eftir ${item.label || 'Óþekkt'}`}
                onClick={() => handleSeriesFilter(item)}
              >
                <span
                  className="h-3 w-3 shrink-0 rounded-sm"
                  style={{ backgroundColor: comparisonPalette[index % comparisonPalette.length] }}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{item.label || 'Óþekkt'}</span>
                  <span className="block text-muted-foreground text-xs">
                    {formatTrendValue(item.points.at(-1)?.value ?? 0, metric)} <BarChart3 className="inline h-3 w-3" />
                  </span>
                </span>
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
